/**
 * Thin React bindings for the design system's CSS classes.
 *
 * The DAC.Infomotion system ships its application layer as plain classes in
 * `tokens/app.css`, so these components carry no styling of their own - they
 * only pick the right class names. Restyling therefore happens by re-syncing
 * the vendored tokens, never by editing this file.
 */
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from "react";

/** A Material Symbols glyph, the icon face the design system specifies. */
export function Icon({ name, size = 20, className = "" }: { name: string; size?: number; className?: string }) {
  return (
    <span className={`dac-icon ${className}`} style={{ fontSize: size }} aria-hidden="true">
      {name}
    </span>
  );
}

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
type ControlSize = "sm" | "md" | "lg";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ControlSize;
  icon?: string;
  children?: ReactNode;
}

export function Button({
  variant = "secondary",
  size = "md",
  icon,
  children,
  className = "",
  ...rest
}: ButtonProps) {
  const sizeClass = size === "md" ? "" : `dac-btn--${size}`;
  return (
    <button className={`dac-btn dac-btn--${variant} ${sizeClass} ${className}`.trim()} {...rest}>
      {icon && <Icon name={icon} size={size === "lg" ? 20 : 16} />}
      {children}
    </button>
  );
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: string;
  /** Required: an icon-only control is unusable to a screen reader without it. */
  label: string;
  size?: "sm" | "md";
  active?: boolean;
}

export function IconButton({ icon, label, size = "md", active, className = "", ...rest }: IconButtonProps) {
  return (
    <button
      className={`dac-iconbtn ${size === "sm" ? "dac-iconbtn--sm" : ""} ${className}`.trim()}
      aria-label={label}
      title={label}
      aria-pressed={active}
      {...rest}
    >
      <Icon name={icon} size={size === "sm" ? 16 : 20} />
    </button>
  );
}

interface FieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  icon?: string;
  hint?: string;
  size?: "md" | "lg";
  boxClassName?: string;
}

export function Field({ label, icon, hint, size = "md", boxClassName = "", className = "", ...rest }: FieldProps) {
  return (
    <label className={`dac-field ${className}`.trim()}>
      {label && <span className="dac-field__label">{label}</span>}
      <span className={`dac-field__box ${size === "lg" ? "dac-field__box--lg" : ""} ${boxClassName}`.trim()}>
        {icon && (
          <span className="dac-field__affix">
            <Icon name={icon} size={16} />
          </span>
        )}
        <input className="dac-field__input" {...rest} />
      </span>
      {hint && <span className="dac-field__hint">{hint}</span>}
    </label>
  );
}

type Tone = "success" | "info" | "warning" | "danger" | "neutral";

export function Badge({ tone = "neutral", label, dot }: { tone?: Tone; label: string; dot?: boolean }) {
  return (
    <span className={`dac-badge dac-badge--${tone}`}>
      {dot && <span className="dac-badge__dot" />}
      {label}
    </span>
  );
}

export function Chip({
  label,
  onClick,
  onRemove,
  active,
}: {
  label: string;
  onClick?: () => void;
  onRemove?: () => void;
  active?: boolean;
}) {
  return (
    <button className={`dac-chip ${active ? "dac-chip--on" : ""}`.trim()} onClick={onClick} type="button">
      <span className="u-truncate">{label}</span>
      {onRemove && (
        <span
          className="dac-chip__x"
          role="button"
          aria-label={`Remove ${label}`}
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
        >
          <Icon name="close" size={14} />
        </span>
      )}
    </button>
  );
}

export function Switch({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      className="dac-switch"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
    >
      <span className={`dac-switch__track ${checked ? "dac-switch__track--on" : ""}`.trim()}>
        <span className="dac-switch__knob" />
      </span>
      {label}
    </button>
  );
}

export function Spinner({ label }: { label: string }) {
  return (
    <span className="u-row u-muted" style={{ fontSize: "var(--text-ui-size)" }}>
      <Icon name="progress_activity" size={16} />
      {label}
    </span>
  );
}
